import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { IntelTopicCluster } from '../model/contracts'

const SENTIMENT_DOT: Record<string, string> = {
  positive: 'bg-sentiment-positive',
  negative: 'bg-sentiment-negative',
  neutral: 'bg-sentiment-neutral',
}

function sentimentDotClass(sentiment: string): string {
  const key = sentiment.toLowerCase()
  return SENTIMENT_DOT[key] ?? 'bg-sentiment-neutral'
}

interface ClusterCardProps {
  cluster: IntelTopicCluster
}

function ClusterCard({ cluster }: ClusterCardProps) {
  const { t } = useTranslation('intel')
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="flex flex-col rounded-xl border border-border bg-card shadow-sm">
      <div className="flex items-center gap-2 p-4 pb-3">
        <span
          className={cn(
            'size-2.5 shrink-0 rounded-full',
            sentimentDotClass(cluster.sentiment),
          )}
        />
        <p className="text-sm font-semibold text-foreground">{cluster.label}</p>
      </div>

      <div className="px-4 pb-3">
        <p className="text-sm text-muted-foreground">{cluster.summary}</p>
      </div>

      {cluster.exampleQuotes.length > 0 && (
        <div className="border-t border-border">
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            className="flex w-full items-center justify-between px-4 py-2.5 text-xs font-medium text-muted-foreground hover:text-foreground"
          >
            <span>{t('clusters.examples')}</span>
            <ChevronDown
              size={14}
              className={cn('transition-transform', expanded && 'rotate-180')}
            />
          </button>
          {expanded && (
            <ul className="flex flex-col gap-2 px-4 pb-3">
              {cluster.exampleQuotes.map((quote, i) => (
                <li key={i}>
                  <blockquote className="border-l-2 border-border pl-3 text-xs italic text-muted-foreground">
                    {quote}
                  </blockquote>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {cluster.recommendation && (
        <div className="mx-4 mb-4 mt-1 rounded-lg bg-muted px-3 py-2.5">
          <p className="mb-0.5 text-xs font-semibold text-foreground">
            {t('clusters.recommendation')}
          </p>
          <p className="text-xs text-muted-foreground">{cluster.recommendation}</p>
        </div>
      )}
    </div>
  )
}

interface IntelTopicClustersProps {
  clusters: IntelTopicCluster[]
}

export function IntelTopicClusters({ clusters }: IntelTopicClustersProps) {
  const { t } = useTranslation('intel')

  if (clusters.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
        <p className="text-sm text-muted-foreground">{t('clusters.empty')}</p>
      </div>
    )
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {clusters.map((cluster) => (
        <ClusterCard key={cluster.id} cluster={cluster} />
      ))}
    </div>
  )
}
